import { useEffect, useState } from 'react';
import { X, CheckSquare, Square, Loader2 } from 'lucide-react';
import { DOCUMENT_TYPES } from '../data/gestionDocumentaireTypes';
import { getTemplateDocumentLinks, setTemplateDocumentLinks } from '../services/intakeTemplateLinksService';

interface TemplateDocumentLinksModalProps {
  isOpen: boolean;
  onClose: () => void;
  templateId: string;
  templateName: string;
}

export default function TemplateDocumentLinksModal({
  isOpen,
  onClose,
  templateId,
  templateName,
}: TemplateDocumentLinksModalProps) {
  const [selectedCodes, setSelectedCodes] = useState<string[]>([]);
  const [, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !templateId) return;

    const loadLinks = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const codes = await getTemplateDocumentLinks(templateId);
        setSelectedCodes(codes);
      } catch (e: any) {
        console.error('Erreur chargement liens template / types:', e);
        setError(e?.message || 'Erreur lors du chargement des liens');
      } finally {
        setIsLoading(false);
      }
    };

    loadLinks();
  }, [isOpen, templateId]);

  if (!isOpen) return null;

  const toggleCode = (code: string) => {
    setSelectedCodes((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    try {
      await setTemplateDocumentLinks(templateId, selectedCodes);
      onClose();
    } catch (e: any) {
      console.error('Erreur sauvegarde liens template / types:', e);
      setError(e?.message || 'Erreur lors de la sauvegarde');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-5 py-3 bg-gradient-to-r from-emerald-600 via-bz-blue to-indigo-700 text-white flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold">Types de documents liés</h2>
            <p className="text-[11px] text-white/80">
              Template&nbsp;: <span className="font-semibold">{templateName}</span> ({templateId})
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-white/15 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Contenu */}
        <div className="px-5 py-4 space-y-3 text-xs text-gray-700">
          <p className="text-[11px] text-gray-600">
            Sélectionne les <strong>types de documents PRIsME</strong> pour lesquels cette prise de besoin
            sera utilisée. Tu peux en choisir <strong>un ou plusieurs</strong>.
          </p>

          {error && (
            <div className="px-3 py-2 rounded-md bg-red-50 border border-red-200 text-[11px] text-red-700">
              {error}
            </div>
          )}

          <div className="border border-gray-200 rounded-xl overflow-hidden">
            <div className="bg-gray-50 px-3 py-2 text-[11px] font-semibold text-gray-600 flex">
              <div className="flex-1">Type de document</div>
              <div className="w-28 text-right">Code</div>
            </div>
            <div className="max-h-64 overflow-y-auto divide-y divide-gray-100">
              {DOCUMENT_TYPES.map((dt) => {
                const checked = selectedCodes.includes(dt.code);
                return (
                  <button
                    key={dt.code}
                    type="button"
                    onClick={() => toggleCode(dt.code)}
                    className={`w-full px-3 py-2 text-left flex items-center gap-3 text-[11px] ${
                      checked ? 'bg-emerald-50' : 'bg-white hover:bg-gray-50'
                    }`}
                  >
                    <span className="w-4 h-4 flex items-center justify-center text-emerald-600">
                      {checked ? <CheckSquare className="w-3 h-3" /> : <Square className="w-3 h-3" />}
                    </span>
                    <div className="flex-1">
                      <div className="font-semibold text-gray-800">{dt.title}</div>
                      <div className="text-[10px] text-gray-500 line-clamp-1">{dt.description}</div>
                    </div>
                    <div className="w-28 text-right text-[10px] font-mono text-gray-600">{dt.code}</div>
                  </button>
                );
              })}
              {DOCUMENT_TYPES.length === 0 && (
                <div className="px-3 py-4 text-[11px] text-gray-500 text-center">
                  Aucun type de document défini pour le moment.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between text-[11px]">
          <span className="text-gray-500">
            {selectedCodes.length === 0
              ? 'Aucun type sélectionné'
              : `${selectedCodes.length} type(s) sélectionné(s)`}
          </span>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-3 py-1.5 rounded-full border border-gray-300 text-gray-600 hover:bg-gray-100 transition-colors"
              disabled={isSaving}
            >
              Annuler
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-4 py-1.5 rounded-full bg-emerald-600 text-white font-semibold hover:bg-emerald-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-1.5"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Sauvegarde...
                </>
              ) : (
                'Sauvegarder'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


